import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Typography, Button, Modal, Form, Input, Select, Tag, message } from '../../ui';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';
import { useServerSort } from '../../hooks/useServerSort';

export function TemplatesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const canBroadcast = usePermission('NOTIF.BROADCAST'); // separate from NOTIF.TEMPLATE.ADMIN (the route/page perm)

  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees the
  // same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['notif-templates', sort],
    queryFn: () => request<any[]>({ url: '/notifications/templates', params: sort ? { sort } : {} }),
  });
  const create = useConfirmedMutation({ title: "Save this notification template?", content: "New notifications of this type will use it.", okText: "Yes, save" }, {
    mutationFn: (v: any) => request({ url: '/notifications/templates', method: 'POST', data: v }),
    onSuccess: () => { message.success('Saved'); setOpen(false); qc.invalidateQueries({ queryKey: ['notif-templates'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save template'),
  });
  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Code', dataIndex: 'code', sortable: true, alwaysVisible: true },
    {
      key: 'channel', title: 'Channel', dataIndex: 'channel', sortable: true,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { key: 'language', title: 'Lang', dataIndex: 'language', sortable: true, width: 80 },
    { key: 'subject', title: 'Subject', dataIndex: 'subject', sortable: true },
    {
      key: 'body', title: 'Body', dataIndex: 'body',
      render: (v: string) => (
        <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</div>
      ),
    },
    {
      key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false,
      render: (v: string) => formatIst(v),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Notification Templates</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" onClick={() => setOpen(true)}>New Template</Button>
          {/* Composing a broadcast lives on its own page: it needs a recipient picker, an image
              upload, a deep-link target and a live preview, none of which fit in a modal — and the
              modal's three-item audience list could not address most of the platform's roles. */}
          {canBroadcast && (
            <Button variant="default" size="sm" onClick={() => navigate('/notifications/broadcast')}>
              Send a notification
            </Button>
          )}
        </div>
      </div>
      <Card>
        <DataTable
          storageKey="notif-templates"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>

      <Modal title="New Template" open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <Form layout="vertical" onFinish={(v) => create.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input placeholder="ORDER_PLACED" /></Form.Item>
          <Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
            <Select options={[{ value: 'FCM' }, { value: 'WHATSAPP' }, { value: 'SMS' }, { value: 'EMAIL' }]} />
          </Form.Item>
          <Form.Item name="language" label="Language" initialValue="en"><Input /></Form.Item>
          <Form.Item name="subject" label="Subject"><Input placeholder="Optional" /></Form.Item>
          <Form.Item name="body" label="Body (Handlebars: {{var}})" rules={[{ required: true }]}>
            <Input.TextArea rows={4} placeholder="Hello {{name}}, your order {{orderNo}} of ₹{{amount}} is placed." />
          </Form.Item>
          <Button variant="primary" type="submit" block loading={create.isPending}>Save</Button>
        </Form>
      </Modal>

    </div>
  );
}
